import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { combineLatest, map, shareReplay, withLatestFrom } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

import * as SearchFilterOptionsCurrentSelectors from '../option-ids-selectable/option-ids-selectable.selectors';
import {
  clearAllFilters,
  clearDateFilters,
  clearSearchFilterOptions,
  initDateFilters,
  initOptionIdsVisible,
  optionIdsVisibleSelected,
  optionIdsVisibleUnselected,
  optionIdsVisibleValuesSelected,
  optionIdsVisibleValuesUnselected,
  removeExhibitorFilter,
  searchFilterOptionValueChipRemoved,
  selectedDatetimeFilterEnd,
  selectedDatetimeFilterReset,
  selectedDatetimeFilterStart,
  unselectDateFilter,
} from '../option-ids-visible/option-ids-visible.actions';
import * as SearchFilterOptionsSelectors from '../option-ids-visible/option-ids-visible.selectors';
import {
  DateFilter,
  DeepNestedFilterValue,
  IdentityFilter,
  NestedFilter,
  NestedFilterValue,
  SearchFilter,
  SearchFilterOption,
  SearchFilterOptionsEntity,
  SearchFilterType,
  SimpleFilter,
} from '../option-ids-visible/search-filter-options.models';
import * as SearchFilterLabelsSelectors from '../option-labels/option-labels.selectors';
import {
  SearchFilterLabelsEntity,
  SearchFilterLabelsNestedEntity,
  SearchFilterLabelsSimpleEntity,
  SearchFilterLabelsSort,
} from '../option-labels/search-filter-labels.models';

type WithLabel = Required<{
  key: string;
  label?: string;
  personalized: boolean;
  count?: number;
  disabled?: boolean;
}>;

interface NestedFliterValueWithPath extends NestedFilterValue {
  root_path: string[];
  external_id: string;
}

@Injectable()
export class OptionsFacade {
  /**
   * Combine pieces of state using createSelector,
   * and expose them as observables through the facade.
   */
  loaded$ = this.store.pipe(select(SearchFilterOptionsSelectors.getOptionIsVisibleLoaded));
  allSearchFilterOptions$: Observable<SearchFilter[]> = combineLatest([
    this.store.pipe(select(SearchFilterOptionsSelectors.getAllOptionIdsVisible)),
    this.store.pipe(select(SearchFilterOptionsCurrentSelectors.getAllOptionIdsSelectable)),
    this.store.pipe(select(SearchFilterLabelsSelectors.getOptionLabelsEntities)),
  ]).pipe(
    withLatestFrom(this.store.pipe(select(SearchFilterOptionsSelectors.getSelectedOptionIdsVisible))),
    map(([[filterOptions, filterOptionsCurrent, filterLabels], selectedOptionIdsVisible]) => {
      return this.sortFilterDisplayValues(
        this.markDisabledFilters(
          this.combineFilterOptionAndLabel(filterOptions, filterLabels),
          filterOptionsCurrent,
          selectedOptionIdsVisible
        )
      );
    }),
    shareReplay(1)
  );
  selectedOptionIdsVisible$ = this.store.pipe(select(SearchFilterOptionsSelectors.getSelectedOptionIdsVisible));
  allAvailableFilters$ = this.allSearchFilterOptions$.pipe(
    map((filterTypes) => {
      const availableFilters: { type: SearchFilter; filter: { key: string; label: string } }[] = [];
      for (const type of filterTypes) {
        switch (type.filterType) {
          case SearchFilterType.identity:
            for (const filter of type.values) {
              availableFilters.push({ type, filter });
            }
            break;
          case SearchFilterType.simple:
            for (const value of type.values) {
              availableFilters.push({ type, filter: { key: value.key, label: value.key } });
            }
            break;
          case SearchFilterType.nested:
            for (const group of type.values) {
              if (group.values.length) {
                for (const filter of group.values) {
                  availableFilters.push({ type, filter });
                }
              } else {
                availableFilters.push({ type: type, filter: { key: group.key, label: group.label } });
              }
            }
            break;
          default:
            break;
        }
      }
      return availableFilters;
    })
  );
  activeFacet$ = this.store.pipe(select(SearchFilterOptionsSelectors.getSelectedFacet));
  selectedEventDatetimeFilter$ = this.store.pipe(select(SearchFilterOptionsSelectors.getSelectedEventDatetimeFilter));
  eventStartDateFilter$ = this.store.pipe(select(SearchFilterOptionsSelectors.getSelectedEventStartDate));
  eventEndDateFilter$ = this.store.pipe(select(SearchFilterOptionsSelectors.getSelectedEventEndDate));

  constructor(private readonly store: Store) {}

  onInitFilterOptions(selectedFilterOptions: { [key: string]: string[] }) {
    this.store.dispatch(initOptionIdsVisible({ selectedFilterOptions }));
  }

  onInitDateFilters(dateFilter: DateFilter) {
    this.store.dispatch(initDateFilters({ dateFilter }));
  }

  onClear() {
    this.store.dispatch(clearAllFilters());
  }

  onClearFilterOptions() {
    this.store.dispatch(clearSearchFilterOptions());
  }

  onClearDateFilters() {
    this.store.dispatch(clearDateFilters());
  }

  onUnselectDateFilter() {
    this.store.dispatch(unselectDateFilter());
  }

  onRemoveExhibitorFilter() {
    this.store.dispatch(removeExhibitorFilter());
  }

  onSelectSearchFilterOption(searchFilterOption: SearchFilterOption) {
    this.store.dispatch(optionIdsVisibleSelected({ searchFilterOption: searchFilterOption }));
  }

  onUnselectActiveFilterOption() {
    this.store.dispatch(optionIdsVisibleUnselected());
  }

  onSelectDatetimeFilterStart(lower: number) {
    this.store.dispatch(selectedDatetimeFilterStart({ lower }));
  }

  onSelectDatetimeFilterEnd(upper: number) {
    this.store.dispatch(selectedDatetimeFilterEnd({ upper }));
  }

  onSelectDatetimeFilterReset() {
    this.store.dispatch(selectedDatetimeFilterReset());
  }

  onSelectFilterOptionValues(
    searchFilterOption: SearchFilter,
    searchFilterOptionValues: { key: string }[],
    checked: boolean
  ) {
    if (checked) {
      this.store.dispatch(optionIdsVisibleValuesSelected({ searchFilterOption, searchFilterOptionValues }));
    } else {
      this.store.dispatch(optionIdsVisibleValuesUnselected({ searchFilterOption, searchFilterOptionValues }));
    }
  }

  onRemoveFilterOptionValueChip(searchFilterOption: SearchFilter, searchFilterOptionValue: { key: string }) {
    this.store.dispatch(searchFilterOptionValueChipRemoved({ searchFilterOption, searchFilterOptionValue }));
  }

  private combineFilterOptionAndLabel(
    filterOptions: SearchFilterOptionsEntity[],
    filterLabels: Dictionary<SearchFilterLabelsEntity>
  ): SearchFilter[] {
    return filterOptions
      .filter((filterOption) => !this.isMissingLabels(filterOption, filterLabels))
      .map((filterOption) => {
        switch (filterOption.filterType) {
          case SearchFilterType.simple:
            return this.mapSimpleStringFilter(filterOption);
          case SearchFilterType.identity:
            return this.mapIdentityStringFilter(
              filterOption,
              filterLabels[filterOption.key] as SearchFilterLabelsSimpleEntity
            );
          case SearchFilterType.nested:
            return this.filterEmptyFiltersFromTree(
              this.mapNestedIdentityStringFilter(
                filterOption,
                filterLabels[filterOption.key] as SearchFilterLabelsNestedEntity
              )
            );
        }
      });
  }

  private mapSimpleStringFilter(filterOption: SearchFilterOptionsEntity): SimpleFilter {
    return {
      ...filterOption,
      filterType: SearchFilterType.simple,
    };
  }

  private mapIdentityStringFilter(
    filterOption: SearchFilterOptionsEntity,
    filterLabels: SearchFilterLabelsSimpleEntity | { values: { key: string; label: string }[] }
  ): IdentityFilter {
    const sortOrder = Array.isArray(filterLabels.values)
      ? undefined
      : (<SearchFilterLabelsSimpleEntity>filterLabels).sortOrder;
    return {
      sortOrder,
      key: filterOption.key,
      filterType: SearchFilterType.identity,
      icon: filterOption.icon,
      label: filterOption.label,
      values: filterOption.values
        .map((filterOptionValue) => {
          if (Array.isArray(filterLabels.values)) {
            return {
              ...filterOptionValue,
              label: filterLabels.values.find((label) => label.key === filterOptionValue.key)?.label,
            };
          }
          return {
            ...filterOptionValue,
            label: filterLabels.values[filterOptionValue.key],
          };
        })
        .filter((value): value is WithLabel => !!value.label),
    };
  }

  private isDefined<T>(item: T | undefined): item is T {
    return !!item;
  }

  private recursiveFindParent(
    list: DeepNestedFilterValue[] | undefined,
    element: DeepNestedFilterValue
  ): DeepNestedFilterValue | undefined {
    if (!list) {
      return undefined;
    }
    const nextElement = list
      .filter(this.isDefined)
      .find((possibleParent) => element.external_id.startsWith(possibleParent.external_id + '.'));

    if (!nextElement) {
      return undefined;
    }
    if (nextElement.external_id === element.root_path.join('.')) {
      return nextElement;
    } else {
      return this.recursiveFindParent(nextElement.values, element);
    }
  }

  private mapNestedIdentityStringFilter(
    filterOption: SearchFilterOptionsEntity,
    filterLabels: SearchFilterLabelsNestedEntity
  ): NestedFilter {
    let fullResultList: NestedFilterValue[] = [];
    const filterLabelsWithAddedOptions: NestedFilterValue[] = filterLabels.values
      .map((filterLabelValue) => {
        const option = filterOption.values.find((value) => value.key === filterLabelValue.key);
        return {
          key: filterLabelValue.key,
          external_id: filterLabelValue.external_id,
          label: filterLabelValue.label,
          count: option?.count ?? 0,
          personalized: option?.personalized ?? false,
          root_path: filterLabelValue.root_path,
          values: [],
        };
      })
      .sort(this.compareExternalIds);

    fullResultList = filterLabelsWithAddedOptions.reduce((resultList, value) => {
      if (value.root_path.length === 0) {
        resultList.push(value);
      } else {
        const parentResult = this.recursiveFindParent(resultList, value);
        if (!parentResult) {
          return resultList;
        }
        if (parentResult.values) {
          parentResult.values.push(value);
        } else {
          parentResult.values = [value];
        }
      }
      return resultList;
    }, [] as NestedFliterValueWithPath[]);

    return {
      key: filterOption.key,
      filterType: SearchFilterType.nested,
      label: filterOption.label,
      icon: filterOption.icon,
      values: fullResultList,
      sortOrder: filterLabels.sortOrder,
    };
  }

  compareExternalIds(a: NestedFilterValue, b: NestedFilterValue): number {
    const partsA = a.external_id.split('.').map(Number);
    const partsB = b.external_id.split('.').map(Number);

    for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
      const diff = partsA[i] - partsB[i];
      if (diff !== 0) {
        return diff;
      }
    }

    return partsA.length - partsB.length;
  }

  private isMissingLabels(
    filterOption: SearchFilterOptionsEntity,
    filterLabels: Dictionary<SearchFilterLabelsEntity>
  ): boolean {
    return filterOption.filterType !== 'SIMPLE' && filterLabels[filterOption.key] === undefined;
  }

  private filterEmptyFiltersFromTree(filterTree: NestedFilter): NestedFilter {
    const reducedFilterTree = filterTree.values.filter((filter) => {
      if (filter.values.length) {
        filter.values = this.filterEmptyFilterValuesInFilter(filter.values);
      }
      return filter.count !== 0 || filter.values.length;
    });
    return { ...filterTree, values: reducedFilterTree };
  }

  private filterEmptyFilterValuesInFilter(nestedFilter: DeepNestedFilterValue[]): DeepNestedFilterValue[] {
    return nestedFilter.filter((nestedFil) => {
      if (nestedFil.values?.length) {
        nestedFil.values = this.filterEmptyFilterValuesInFilter(nestedFil.values);
      }
      if (!nestedFil.count && !nestedFil.values?.length) {
        return false;
      }
      return nestedFil;
    });
  }

  private markDisabledFilters(
    allFilters: SearchFilter[],
    currentFilters: SearchFilterOptionsEntity[],
    selectedOptionIdsVisible: { [key: string]: string[] }
  ): SearchFilter[] {
    return allFilters.map((filter) => {
      const currentFilter = currentFilters.find((currentFilter) => currentFilter.key === filter.key);
      if (currentFilter) {
        filter = this.markDisabledFilterValuesInFilter(filter, currentFilter, selectedOptionIdsVisible);
        return { ...filter, disabled: filter.values.every((value) => value.disabled) };
      }
      return { ...filter, disabled: true };
    });
  }

  private markDisabledFilterValuesInFilter(
    filter: SearchFilter,
    currentFilter: SearchFilterOptionsEntity,
    selectedOptionIdsVisible: { [key: string]: string[] }
  ): SearchFilter {
    if (filter.filterType === SearchFilterType.nested) {
      filter.values = filter.values.map((filterValueFirstLevel) => {
        filterValueFirstLevel.values = this.markDisabledFilterValues(
          filterValueFirstLevel.values,
          currentFilter,
          selectedOptionIdsVisible
        );
        if (filterValueFirstLevel.values.length === 0) {
          filterValueFirstLevel.disabled = !currentFilter.values.find(
            (currentFilterValue) => currentFilterValue.key === filterValueFirstLevel.key
          );
        } else {
          filterValueFirstLevel.disabled = !filterValueFirstLevel.values.some((markedValue) => !markedValue.disabled);
        }

        return filterValueFirstLevel;
      });
    } else {
      filter.values = this.markDisabledFilterValues(filter.values, currentFilter, selectedOptionIdsVisible);
    }
    return filter;
  }

  private markDisabledFilterValues<ValueType extends { key: string; values?: DeepNestedFilterValue[] }>(
    filterValues: ValueType[],
    currentFilter: SearchFilterOptionsEntity,
    selectedOptionIdsVisible: { [key: string]: string[] }
  ): ValueType[] {
    return filterValues.map((filter) => {
      if (filter.values && filter.values.length > 0) {
        const values = this.markDisabledFilterValues(filter.values, currentFilter, selectedOptionIdsVisible);
        const disabled = !values.some((value) => !value.disabled);
        return { ...filter, disabled, values };
      }
      if (currentFilter.values.find((currentFilterValue) => currentFilterValue.key === filter.key)) {
        return { ...filter, disabled: false };
      } else if (selectedOptionIdsVisible[currentFilter.key]?.includes(filter.key)) {
        return { ...filter, disabled: false };
      } else {
        return { ...filter, disabled: true };
      }
    });
  }

  private sortFilterDisplayValues(filterOptions: SearchFilter[]): SearchFilter[] {
    return filterOptions.map((filter) => {
      switch (filter.filterType) {
        case SearchFilterType.simple:
          return this.sortSimpleFilterValues(filter);
        case SearchFilterType.identity:
          return this.sortIdentityFilterValues(filter);
        case SearchFilterType.nested:
          return this.sortNestedFilterValues(filter);
      }
    });
  }

  private sortSimpleFilterValues(filter: SimpleFilter): SearchFilter {
    return {
      ...filter,
      values: filter.values.slice().sort(this.compareByKey),
    };
  }

  private sortIdentityFilterValues(filter: IdentityFilter): SearchFilter {
    return {
      ...filter,
      values:
        filter.sortOrder === SearchFilterLabelsSort.fixed
          ? filter.values.slice()
          : filter.values.slice().sort(this.compareByLabel),
    };
  }

  private sortNestedFilterValues(filter: NestedFilter): SearchFilter {
    return {
      ...filter,
      values:
        filter.sortOrder === SearchFilterLabelsSort.fixed
          ? filter.values.slice()
          : filter.values
              .slice()
              .sort(this.compareByLabel)
              .map((nested) => ({
                ...nested,
                values: nested.values.slice().sort(this.compareByLabel),
              })),
    };
  }

  private compareByKey(
    value: { key: string; personalized: boolean },
    otherValue: { key: string; personalized: boolean }
  ) {
    return value.key.trim().localeCompare(otherValue.key.trim());
  }

  private compareByLabel(
    value: { label: string; personalized: boolean },
    otherValue: { label: string; personalized: boolean }
  ) {
    return value.label.trim().localeCompare(otherValue.label.trim());
  }
}
